// src/components/legal/PrivacyPolicy.tsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import SparkLayout from '../layouts/SparkLayout';
import SparkCard from '../common/SparkCard';
import SparkButton from '../common/SparkButton';
import SparkHeading from '../common/SparkHeading';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <SparkLayout>
      <SparkCard className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <SparkHeading>Privacy Policy</SparkHeading>
          <SparkButton
            variant="secondary"
            onClick={() => navigate(-1)}
            className="text-sm"
          >
            BACK
          </SparkButton>
        </div>
        
        <div className="prose prose-invert max-w-none text-white/90 space-y-6 overflow-y-auto max-h-[70vh] pr-4">
          <p className="text-sm opacity-75">Last Updated: July 8, 2025</p>
          
          <p>At Spark Invites ("we," "us," or "our"), we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our website, services, or products (collectively, the "Services") to create custom personalized video invitations. By using our Services, you consent to the practices described in this Privacy Policy.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">1. Information We Collect</h2>
          <p>We may collect the following types of information:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Personal Information:</strong> Information that identifies you, such as your name, email address, phone number, and payment information, provided when you create an account, place an order, or contact us.</li>
            <li><strong>User-Generated Content:</strong> Content you upload or provide, such as images, text, or videos, to create personalized video invitations.</li>
            <li><strong>Usage Data:</strong> Information about how you interact with our Services, including IP addresses, browser type, pages visited, and timestamps, collected automatically through cookies or similar technologies.</li>
            <li><strong>Mobile Information:</strong> If you opt in to receive SMS/MMS notifications, we collect your mobile phone number and related opt-in data.</li>
          </ul>

          <h2 className="text-xl font-bold mt-8 mb-4">2. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide, operate, and improve our Services, including creating and delivering your custom video invitations.</li>
            <li>Process payments and fulfill orders.</li>
            <li>Send you important updates, such as order confirmations or account-related information.</li>
            <li>Communicate with you, including sending SMS/MMS notifications (if you opt in) containing important information, updates, deals, and specials, with up to 20 messages per month.</li>
            <li>Analyze usage patterns to enhance user experience and optimize our Services.</li>
            <li>Comply with legal obligations and protect the security of our Services.</li>
          </ul>

          <h2 className="text-xl font-bold mt-8 mb-4">3. Sharing of Information</h2>
          <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Service Providers:</strong> With trusted third-party service providers (e.g., payment processors or hosting services) who assist us in operating our Services, provided they agree to keep your information confidential.</li>
            <li><strong>Legal Requirements:</strong> When required by law, such as to comply with a subpoena, court order, or other legal process.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, where your information may be transferred as part of the transaction.</li>
            <li><strong>No Sharing of Mobile Information:</strong> No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. All the above categories exclude text messaging originator opt-in data consent: this information will not be shared with any third parties.</li>
          </ul>

          <h2 className="text-xl font-bold mt-8 mb-4">4. SMS/MMS Notifications</h2>
          <p>By providing your mobile number, you consent to receive SMS/MMS messages from Spark Invites containing important information, updates, deals, and specials. You will receive up to 20 messages per month. Message and data rates may apply, and you may incur charges from your mobile provider depending on your plan. You may opt out at any time by texting STOP to 19563596717, and you may receive a confirmation message of your opt-out. For questions about your text or data plan, contact your wireless provider.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">5. Cookies and Tracking Technologies</h2>
          <p>We use cookies and similar technologies to collect usage data, enhance user experience, and analyze website performance. You can manage cookie preferences through your browser settings, but disabling cookies may affect the functionality of our Services.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">6. Data Security</h2>
          <p>We implement reasonable technical and organizational measures to protect your personal information from unauthorized access, loss, or misuse. However, no system is completely secure, and we cannot guarantee absolute security.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">7. Your Choices</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account Information:</strong> You may update or delete your account information by logging into your account or contacting us.</li>
            <li><strong>SMS/MMS Opt-Out:</strong> You can opt out of SMS/MMS notifications by texting STOP to 19563596717.</li>
            <li><strong>Cookies:</strong> You can disable cookies through your browser settings, though this may impact your experience with our Services.</li>
          </ul>

          <h2 className="text-xl font-bold mt-8 mb-4">8. Children's Privacy</h2>
          <p>Our Services are not intended for individuals under 13 years of age. We do not knowingly collect personal information from children under 13. If we learn that we have collected such information, we will take steps to delete it.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">9. International Users</h2>
          <p>Our Services are hosted in the United States. If you access our Services from outside the U.S., your information may be transferred to and processed in the U.S., where data protection laws may differ from those in your country.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">10. Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of significant changes by posting the updated policy on our website with a revised "Last Updated" date.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">11. Contact Us</h2>
          <p>If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Email: natalie@sparkinvites.com</li>
            <li>Phone: (561) 570-5313</li>
          </ul>

          <p className="mt-8">By using our Services, you acknowledge that you have read and understood this Privacy Policy.</p>
        </div>
      </SparkCard>
    </SparkLayout>
  );
};

export default PrivacyPolicy;